import React, { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useParams } from 'react-router-dom';
import { DownloadFile } from 'components/common/download-files/DownloadFilesTable';
import { FileInfo } from 'types/policyService';
import { downloadByUrl } from '@insurancemenu/imenu-components-lib/dist/helpers/downloadByUrl';
import { toJS } from 'mobx';
import { useProductOutputsPageStore } from 'store/pages/product-outputs/pageStore';
import ZipFileSection from 'components/common/download-files/ZipFileSection';
import TableInfoBar, { TotalFilesLabel } from 'components/common/download-files/TableInfoBar';
import { ActionModal, OverlaySpinner } from '@insurancemenu/imenu-components-lib';
import { ModalType } from '@insurancemenu/imenu-components-lib/dist/helpers/ModalsHelper';
import { getZipAvailabilityNote } from 'global/utils/fileAvailability';
import { DownloadFilesTableStyled } from './styles';

const toDownloadFiles = (filesList: FileInfo[]): DownloadFile[] =>
  filesList.map((file) => ({
    id: file.link,
    name: file.name,
    availability: file.availability,
  }));

const toZipDownloadFile = (zip: FileInfo | null): DownloadFile | undefined =>
  zip?.link ? { id: `${zip.type}_${zip.name}`, name: zip.name, availability: zip.availability } : undefined;

const BenefitsTable = observer((): JSX.Element => {
  const store = useProductOutputsPageStore();

  const {
    isWarningModalOpen,
    isLoading,
    benefitSummaries,
    productsJson,
    benefitsZip,
    jsonZip,
    getInitialData,
    resetInitialData,
    setWarningModal,
  } = store;

  const { periodId } = useParams<{ periodId: string }>();
  const benefitsZipFile = toJS(benefitsZip);
  const jsonZipFile = toJS(jsonZip);
  const benefitsZipDownload = toZipDownloadFile(benefitsZipFile);
  const jsonZipDownload = toZipDownloadFile(jsonZipFile);

  useEffect(() => {
    void getInitialData(periodId);
    return resetInitialData;
  }, []);

  const closeWarningModal = (): void => {
    setWarningModal(false);
  };

  const downloadZip = (zip: FileInfo | null): void => {
    if (zip?.link) {
      downloadByUrl(zip.link);
    }
  };

  return (
    <>
      {isLoading ? (
        <OverlaySpinner display={isLoading} overlay="none" />
      ) : (
        <>
          {benefitsZipDownload && (
            <>
              <TableInfoBar title="Final ZIP" rightContent={getZipAvailabilityNote(benefitsZipDownload)} />
              <ZipFileSection
                file={benefitsZipDownload}
                loading={isLoading}
                onDownload={(): void => downloadZip(benefitsZipFile)}
              />
            </>
          )}
          <TableInfoBar
            title="Benefits Summaries"
            rightContent={<TotalFilesLabel filesAmount={benefitSummaries.length} />}
          />
          <DownloadFilesTableStyled
            loading={isLoading}
            filesList={toDownloadFiles(benefitSummaries)}
            onDownload={downloadByUrl}
          />

          {jsonZipDownload && (
            <>
              <TableInfoBar title="Final ZIP" rightContent={getZipAvailabilityNote(jsonZipDownload)} />
              <ZipFileSection
                file={jsonZipDownload}
                loading={isLoading}
                onDownload={(): void => downloadZip(jsonZipFile)}
              />
            </>
          )}
          <TableInfoBar title="Products JSON" rightContent={<TotalFilesLabel filesAmount={productsJson.length} />} />
          <DownloadFilesTableStyled
            loading={isLoading}
            filesList={toDownloadFiles(productsJson)}
            onDownload={downloadByUrl}
          />
          <ActionModal
            show={isWarningModalOpen}
            onClose={closeWarningModal}
            title={
              <span>
                Proposal output files are currently being generated. <br /> Please check back in a minute.
              </span>
            }
            modalType={ModalType.NOTIFICATION}
          />
        </>
      )}
    </>
  );
});

export default BenefitsTable;
